import Color from "color";
import type { CSSProperties } from "react";

import { color, staticVizOverrides } from "metabase/ui/colors";
import type { VisualizationSettings } from "metabase-types/api";

/**
 * Card-level text styling for dashboard `text` and `heading` cards.
 *
 * Font size is a *scale factor*, never an absolute size. Two things depend on that:
 * a single-row text card already drops to `font-size: 0.8em` to stop it clipping
 * (metabase#31613), and markdown's own type scale is `em`-based (`h1: 1.831em` down to
 * `p: 1.143em`). A multiplier composes with both; an absolute size overrides the first and
 * flattens the second.
 *
 * Everything here is emitted as CSS custom properties on one wrapper element rather than as
 * inline styles on the content: react-markdown renders arbitrary tags, so inline styling
 * would mean a `components` map covering a dozen elements, all of them fighting
 * `.text-card-markdown`'s descendant rules on specificity. Custom properties inherit through
 * the whole subtree for the cost of one attribute.
 */

export const FONT_SIZE_SCALES = {
  small: 0.875,
  medium: 1,
  large: 1.25,
  x_large: 1.5,
} as const;

export const FONT_WEIGHTS = {
  light: 300,
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const;

export const DEFAULT_FONT_SIZE = "medium";
export const DEFAULT_FONT_WEIGHT = "default";
export const DEFAULT_FONT_STYLE = "normal";

/** Heading cards render at this size when no font size is chosen. */
export const HEADING_BASE_FONT_SIZE_REM = 1.375;

/**
 * The keys this module reads. Frozen and asserted in the unit spec so an upstream rebase
 * that silently drops one fails loudly instead of quietly un-styling every card.
 */
export const TEXT_CARD_STYLE_KEYS = [
  "text.font_size",
  "text.font_weight",
  "text.font_style",
  "text.text_color",
] as const;

/**
 * The default text colour, resolved from the live palette on every call so that whitelabel
 * and theme changes are honoured. A colour the user *chooses* is stored as a frozen hex and
 * will not follow a later palette change — the same trade every other colour setting in
 * MetrioniQ makes.
 *
 * `.hex()` rather than the raw token because html2canvas-pro (the browser PDF export) copes
 * poorly with anything more exotic than hex/rgb.
 */
export function getDefaultTextColor(): string {
  // staticVizOverrides avoids an alpha colour, matching how waterfall.total_color resolves
  return Color(color("text-primary", staticVizOverrides)).hex();
}

export function getFontSizeScale(settings: VisualizationSettings): number {
  const key = settings["text.font_size"] as keyof typeof FONT_SIZE_SCALES;
  return FONT_SIZE_SCALES[key] ?? FONT_SIZE_SCALES.medium;
}

/**
 * The heading font size as a CSS length, for `measureTextWidth`. The inline-filter list
 * decides when to collapse by measuring the heading text, so it has to measure at the size
 * the heading is actually drawn at or the filters overlap the text.
 */
export function getHeadingFontSize(settings: VisualizationSettings): string {
  return `${HEADING_BASE_FONT_SIZE_REM * getFontSizeScale(settings)}rem`;
}

export function getHeadingFontWeight(settings: VisualizationSettings): number {
  const key = settings["text.font_weight"] as keyof typeof FONT_WEIGHTS;
  return FONT_WEIGHTS[key] ?? FONT_WEIGHTS.bold;
}

/**
 * The custom properties for a card's chosen styling.
 *
 * Returns `{}` when every setting is at its default, so an untouched card renders with no
 * `style` attribute at all and its DOM is byte-identical to before this feature existed.
 * That is the upgrade-safety guarantee, and it is asserted in the unit spec.
 */
export function getTextCardStyle(
  settings: VisualizationSettings = {},
): CSSProperties {
  const style: Record<string, string | number> = {};

  const fontSize = settings["text.font_size"];
  if (fontSize && fontSize !== DEFAULT_FONT_SIZE) {
    const scale = FONT_SIZE_SCALES[fontSize as keyof typeof FONT_SIZE_SCALES];
    if (scale != null) {
      style["--mb-text-card-font-scale"] = scale;
    }
  }

  const fontWeight = settings["text.font_weight"];
  if (fontWeight && fontWeight !== DEFAULT_FONT_WEIGHT) {
    const weight = FONT_WEIGHTS[fontWeight as keyof typeof FONT_WEIGHTS];
    if (weight != null) {
      style["--mb-text-card-weight"] = weight;
    }
  }

  const fontStyle = settings["text.font_style"];
  if (fontStyle && fontStyle !== DEFAULT_FONT_STYLE) {
    style["--mb-text-card-style"] = "italic";
  }

  const textColor = settings["text.text_color"];
  if (typeof textColor === "string" && textColor !== getDefaultTextColor()) {
    style["--mb-text-card-color"] = textColor;
  }

  return style as CSSProperties;
}
